import { useMutation } from "@apollo/client";
import { Camera, CameraResultType } from "@capacitor/camera";
import {
  IonButton,
  IonButtons,
  IonCardSubtitle,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonPage,
  IonRow,
  IonTextarea,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { AuthContext } from "contexts/AuthContext";
import { CREATE_MERCHANT, CREATE_SCAN_KEY } from "graphql/mutations";
import useAccount from "hooks/useAccount";
import useFirebaseUpload from "hooks/useFirebaseUpload";
import useMerchantProfile from "hooks/useMerchantProfile";
import { chevronBackOutline } from "ionicons/icons";
import { useContext, useEffect, useRef, useState } from "react";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import { useHistory } from "react-router";
import { AccountStore } from "stores/AccountStore";
import { thumbnail } from "utils/Utils";
import "./Business.css";

const Business = () => {
  const { authState } = useContext(AuthContext);
  const history = useHistory();
  const account = AccountStore.useState((s) => s.account);
  const [fetchAccount, { data }] = useAccount(authState);
  const { refetch } = useMerchantProfile(authState.user.uid, true);
  const [changed, setChanged] = useState({});
  const imageRef = useRef();
  const [photo, setPhoto] = useState(null);
  const [newAddress, setNewAddress] = useState(null);
  const [{ dataResponse }, setFileData] = useFirebaseUpload();

  const [updateMerchant] = useMutation(CREATE_MERCHANT);
  const [createScanKey] = useMutation(CREATE_SCAN_KEY);

  const updatePhoto = async (url) => {
    const payload = {
      variables: {
        objects: [
          {
            logo_url: url,
          },
        ],
        update: ["logo_url"],
      },
    };

    const res = await updateMerchant(payload);
    if (res)
      AccountStore.update((s) => {
        s.account = { ...s.account, logo_url: url };
      });
    refetch();
  };

  const updateAddress = async (address, location) => {
    const payload = {
      variables: {
        objects: [
          {
            address: address,
            location: location,
          },
        ],
        update: ["address", "location"],
      },
    };

    const res = await updateMerchant(payload);
    if (res)
      AccountStore.update((s) => {
        s.account = { ...s.account, address: address, location: location };
      });
    refetch();
  };

  const takePicture = async () => {
    const image = await Camera.getPhoto({
      quality: 90,
      allowEditing: true,
      resultType: CameraResultType.DataUrl,
    });
    var imageUrl = image.dataUrl;
    setPhoto(image);
    imageRef.current.src = imageUrl;
  };

  useEffect(() => {
    if (dataResponse?.downloadUrl) {
      updatePhoto(dataResponse.downloadUrl);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataResponse]);

  useEffect(() => {
    if (photo) setFileData(photo);
  }, [photo, setFileData]);

  ///end

  const onChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setChanged((prevState) => {
      return { ...prevState, [name]: value };
    });
  };

  const onSave = async () => {
    if (changed.phone) changed["phone"] = changed.phone.replace(/[^0-9]/g, "");

    const payload = {
      variables: {
        objects: [
          {
            ...changed,
          },
        ],
        update: Object.keys(changed),
      },
    };

    await updateMerchant(payload);
    await fetchAccount();
    await refetch();
  };

  useEffect(() => {
    async function genkey(payload) {
      await createScanKey(payload);
      await fetchAccount();
    }
    if (account?.merchant?.oshi_scanner_keys?.length === 0) {
      if (account.merchant?.id) {
        const payload = {
          variables: {
            objects: [
              {
                merchant_id: account.merchant?.id,
              },
            ],
          },
        };
        genkey(payload);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  useEffect(() => {
    if (data?.users_by_pk)
      AccountStore.update((s) => {
        s.account = { ...data.users_by_pk };
      });
  }, [data]);

  const handleChange = (address) => {
    setNewAddress(address);
  };

  const handleSelect = (address) => {
    geocodeByAddress(address)
      .then(async (results) => {
        const coords = await getLatLng(results[0]);
        const location = {
          type: "Point",
          crs: {
            type: "name",
            properties: {
              name: "urn:ogc:def:crs:EPSG::3857",
            },
          },
          coordinates: [coords.lat, coords.lng],
        };
        if (coords.lat) {
          setNewAddress(address);
          updateAddress(address, location);
        }
      })
      .catch((err) => console.error("Error", err));
  };

  return (
    <IonPage className={"business-page-root"}>
      <IonHeader className={"ion-no-border"}>
        <IonToolbar className={"background-color"}>
          <IonButtons slot="start">
            <IonButton onClick={() => history.goBack()}>
              <IonIcon icon={chevronBackOutline} color="dark" size="large" />
            </IonButton>
          </IonButtons>
          <IonTitle>Business</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonGrid>
          <IonRow className="ion-text-center ion-justify-content-center">
            <IonCol
              size="4"
              className="avatar-container animate__animated animate__fadeInTopLeft animate__faster"
            >
              <img
                onClick={() => takePicture()}
                ref={imageRef}
                className={"profile-avatar"}
                src={
                  (account?.merchant?.logo_url &&
                    thumbnail(account?.merchant?.logo_url, 300, 300)) ||
                  require("assets/images/avatar_default.png")
                }
                alt={account?.merchant?.business_name}
              />
            </IonCol>
          </IonRow>
        </IonGrid>
        <IonList lines="none">
          <IonListHeader>
            <IonCardSubtitle>Business Name</IonCardSubtitle>
          </IonListHeader>
          <IonItem>
            <IonInput
              type="text"
              placeholder="Add business name"
              name="business_name"
              value={
                typeof changed?.business_name !== "undefined"
                  ? changed.business_name
                  : account?.merchant?.business_name
              }
              onIonChange={(e) => onChange(e)}
              onIonBlur={() => onSave()}
            ></IonInput>
          </IonItem>
          <IonListHeader>
            <IonCardSubtitle>Business Address</IonCardSubtitle>
          </IonListHeader>
          <IonItem>
            <PlacesAutocomplete
              onChange={(address) => handleChange(address)}
              onSelect={(address) => handleSelect(address)}
              value={newAddress ? newAddress : account?.merchant?.address}
            >
              {({
                getInputProps,
                suggestions,
                getSuggestionItemProps,
                loading,
              }) => (
                <div style={{ width: "100%" }}>
                  <div className="sc-ion-input-ios-h sc-ion-input-ios-s ios hydrated">
                    <input
                      id="autocomplete-input"
                      {...getInputProps({
                        placeholder: "Search places",
                        className: "native-input sc-ion-input-ios ion-input",
                      })}
                    />
                  </div>
                  <div className="autocomplete-dropdown-container">
                    {loading && <div>Loading...</div>}
                    {suggestions.map((suggestion, i) => {
                      const className = suggestion.active
                        ? "suggestion-item--active"
                        : "suggestion-item";
                      // inline style for demonstration purpose
                      const style = suggestion.active
                        ? {
                            cursor: "pointer",
                          }
                        : {
                            paddingLeft: 5,
                            cursor: "pointer",
                          };
                      return (
                        <div
                          key={i}
                          {...getSuggestionItemProps(suggestion, {
                            className,
                            style,
                          })}
                        >
                          <span>{suggestion.description}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </PlacesAutocomplete>
          </IonItem>
          <IonListHeader>
            <IonCardSubtitle>Description</IonCardSubtitle>
          </IonListHeader>
          <IonItem>
            <IonTextarea
              type="text"
              placeholder="Add business description"
              name="description"
              rows={3}
              value={
                typeof changed?.description !== "undefined"
                  ? changed.description
                  : account?.merchant?.description
              }
              onIonChange={(e) => onChange(e)}
              onIonBlur={() => onSave()}
            ></IonTextarea>
          </IonItem>
          <IonListHeader>
            <IonCardSubtitle>Business Contact Info</IonCardSubtitle>
          </IonListHeader>
          <IonItem>
            <IonInput
              type="tel"
              placeholder="Add business phone"
              name="phone_number"
              onIonChange={(e) => onChange(e)}
              onIonBlur={() => onSave()}
              value={
                typeof changed?.phone_number !== "undefined"
                  ? changed.phone_number
                  : account?.merchant?.phone_number
              }
            ></IonInput>
          </IonItem>
          <IonItem>
            <IonInput
              type="text"
              placeholder="Add business email"
              name="email"
              onIonChange={(e) => onChange(e)}
              onIonBlur={() => onSave()}
              value={
                typeof changed?.email !== "undefined"
                  ? changed.email
                  : account?.merchant?.email
              }
            ></IonInput>
          </IonItem>
          <IonItem>
            <IonInput
              type="text"
              placeholder="Add a website"
              name="website"
              onIonChange={(e) => onChange(e)}
              onIonBlur={() => onSave()}
              value={
                typeof changed?.website !== "undefined"
                  ? changed.website
                  : account?.merchant?.website
              }
            ></IonInput>
          </IonItem>
          <IonButton
            routerLink={"/notifications"}
            routerDirection="root"
            expand="block"
            className={"lsp-button"}
            disabled={
              !account?.merchant?.business_name ||
              !account?.merchant?.email ||
              !account?.merchant?.address ||
              !account?.merchant?.phone_number
            }
          >
            <IonLabel className={"ion-text-wrap"}>Continue</IonLabel>
          </IonButton>
        </IonList>
      </IonContent>
    </IonPage>
  );
};

export default Business;
